import { supabase } from '@/integrations/supabase/client'
import { sentryUtils } from './sentry'


// Dynamic API URL based on environment
function getApiUrl(): string {

	// If explicitly set, use that
	if (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL !== 'auto') {
		return import.meta.env.VITE_API_URL
	}

	const PRODUCTION_API_URL = 'https://feedbask-backend.vercel.app'

	try {
		// Auto-detect based on environment
		if (typeof window !== 'undefined') {
			const hostname = window.location.hostname

			// Production
			if (hostname === 'www.feedbask.com' || hostname === 'feedbask.com') {
				return PRODUCTION_API_URL
			}

			// Vercel preview deployment - assumes backend follows same pattern
			if (hostname.includes('.vercel.app')) {
				// Extract git branch from Vercel preview URL
				// Format: polloas-git-auto-fill-feedbask.vercel.app -> feedbask-backend-git-auto-fill-feedbask.vercel.app
				const parts = hostname.split('-')
				if (parts.length >= 4 && parts[1] === 'git') {
					// Extract branch name (everything between 'git' and the scope slug)
					const scopeSlugWithDomain = parts[parts.length - 1] // "feedbask.vercel.app"
					const scopeSlug = scopeSlugWithDomain.replace('.vercel.app', '') // "feedbask"
					const branchParts = parts.slice(2, -1) // ["auto", "fill"]  
					const branchName = branchParts.join('-') // "auto-fill"
					const url = `https://feedbask-backend-git-${branchName}-${scopeSlug}.vercel.app`
					return url
				}
				// Fallback to main backend for unexpected preview URL patterns
				return PRODUCTION_API_URL
			}

			// If localhost hostname, call the Vite dev server (same origin, port 8080);
			// vite.config.ts proxies /api to the backend on 4000.
			if (hostname === 'localhost' || hostname === '127.0.0.1') {
				return ''
			}
		}
	} catch (error) {
	}

	// Ultimate fallback: Always default to production
	return PRODUCTION_API_URL
}

// Lazy evaluation of API_URL to ensure window is available
let API_URL: string | null = null
function getEffectiveApiUrl(): string {
	if (API_URL === null) {
		API_URL = getApiUrl()
	}
	return API_URL
}

// Export this function for use by other modules
export { getEffectiveApiUrl as getApiBaseUrl }

// Auth token cache and request deduplication
let authTokenCache: { token: string | null; timestamp: number } | null = null
let ongoingAuthRequest: Promise<string | null> | null = null
const AUTH_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

async function getAuthToken(): Promise<string | null> {
	const requestStartTime = Date.now()

	try {
		// Check if token in cache is still valid (not expired)
		if (authTokenCache && authTokenCache.token) {
			// Parse JWT to check expiry
			try {
				const tokenPayload = JSON.parse(
					atob(authTokenCache.token.split('.')[1])
				)
				const expiryTime = tokenPayload.exp * 1000 // Convert to milliseconds
				const now = Date.now()
				// Return cached token if it has more than 30 seconds until expiry
				if (expiryTime - now > 30000) {
					return authTokenCache.token
				}
			} catch (e) {
				// Invalid token format, clear cache
				authTokenCache = null
			}
		}

		// Deduplicate concurrent requests
		if (ongoingAuthRequest) {
			return await ongoingAuthRequest
		}

		// Start new auth request
		ongoingAuthRequest = (async () => {
			const supabaseCallStartTime = Date.now()
			try {
				// getSession() will automatically refresh if needed
				const {
					data: { session },
					error,
				} = await supabase.auth.getSession()
				const supabaseCallDuration = Date.now() - supabaseCallStartTime

				if (error) {
					return null
				}

				const token = session?.access_token ?? null

				// Only cache valid tokens
				if (token) {
					authTokenCache = {
						token,
						timestamp: Date.now(),
					}
				} else {
					authTokenCache = null
				}

				return token
			} catch (e) {
				const supabaseCallDuration = Date.now() - supabaseCallStartTime
				throw e // re-throw to be caught by the outer catch
			}
		})()

		const token = await ongoingAuthRequest
		// Clear the promise *after* it has resolved for all awaiters.
		ongoingAuthRequest = null

		return token
	} catch (error: any) {
		// Clear ongoing request on error
		ongoingAuthRequest = null

		if (error.message?.includes('timeout')) {
			authTokenCache = null
		}

		return null
	}
}

// Export function to clear auth cache (useful for logout)
export function clearAuthCache(): void {
	authTokenCache = null
	ongoingAuthRequest = null
}

// Helper to check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
	const token = await getAuthToken()
	const isAuth = !!token
	return isAuth
}

interface FetchOptions extends RequestInit {
	needsAuth?: boolean
	data?: unknown
}

export const apiClient = async <T = any>(
	endpoint: string,
	options: FetchOptions = {}
): Promise<T> => {
	return sentryUtils.startSpan(
		{
			op: 'http.client',
			name: `${options.method || 'GET'} ${endpoint}`,
			attributes: {
				'http.method': options.method || 'GET',
				'http.url': endpoint,
			},
		},
		async (span) => {
			const requestStartTime = Date.now()

			const { needsAuth = true, data, ...fetchOptions } = options

			// Don't set Content-Type for FormData - let browser set it with boundary
			const headers: HeadersInit = {
				...fetchOptions.headers,
			}

			// Only set JSON content type if we're not dealing with FormData
			if (!(fetchOptions.body instanceof FormData)) {
				headers['Content-Type'] = 'application/json'
			}

			if (typeof window !== 'undefined') {
				headers['X-Original-Host'] = window.location.hostname

				try {
					const storedLang = localStorage.getItem('i18nextLng')
					if (storedLang) {
						const lang = storedLang.split('-')[0]
						headers['X-Language'] = lang
					}
				} catch (e) {
				}
			}

			if (needsAuth) {
				try {
					const token = await getAuthToken()

					if (token) {
						headers['Authorization'] = `Bearer ${token}`
					} else {
						// If auth is strictly required and we have no token, fail early.
						const error = new Error(
							'Authentication required but no valid session found'
						) as any
						error.status = 401
						error.code = 'NO_AUTH_TOKEN'
						throw error
					}
				} catch (authError: any) {

					// Clear cache if auth is broken
					if (authError.message?.includes('timeout')) {
						clearAuthCache()
					}

					// Re-throw with consistent error structure
					const error = new Error(
						'Authentication failed: ' + (authError.message || 'Unknown error')
					) as any
					error.status = 401
					error.code = 'AUTH_FETCH_FAILED'
					error.originalError = authError
					throw error
				}
			}

			const config: RequestInit = {
				...fetchOptions,
				headers,
			}

			if (data) {
				config.body = JSON.stringify(data)
			}

			const apiUrl = getEffectiveApiUrl()
			const fullUrl = `${apiUrl}${endpoint}`


			const fetchStartTime = Date.now()
			let response = await fetch(fullUrl, config)
			const fetchTime = Date.now() - fetchStartTime

			// Handle 401 errors by refreshing the token and retrying once
			if (response.status === 401 && needsAuth) {
				// Clear the cache to force a refresh
				clearAuthCache()

				// Try to refresh the session
				const { error: refreshError } = await supabase.auth.refreshSession()

				if (!refreshError) {
					// Get the new token
					const newToken = await getAuthToken()

					if (newToken) {
						// Update headers with new token
						headers['Authorization'] = `Bearer ${newToken}`

						// Retry the request with new token
						const retryConfig: RequestInit = {
							...fetchOptions,
							headers,
						}

						if (data) {
							retryConfig.body = JSON.stringify(data)
						}

						response = await fetch(fullUrl, retryConfig)
					}
				}
			}

			if (!response.ok) {
				let errorData
				try {
					errorData = await response.json()
				} catch (e) {
					errorData = {
						message: `HTTP error ${response.status}: ${response.statusText}`,
					}
				}

				// Set error attributes on span
				span.setAttributes({
					'http.status_code': response.status,
					'error': true,
				})

				const error = new Error(
					errorData?.error ||
					errorData?.message ||
					`Request failed with status ${response.status}`
				) as any
				error.status = response.status
				error.details = errorData?.details
				error.code = errorData?.code

				// Capture error to Sentry
				sentryUtils.captureError(error, {
					endpoint,
					responseStatus: response.status,
					errorData,
				})

				throw error
			}

			// Set success attributes on span
			span.setAttributes({
				'http.status_code': response.status,
			})

			if (response.status === 204) {
				return undefined as T
			}

			const responseData = await response.json()
			return responseData
		})
}

// Type for the widget data expected from the API (should match backend response)
// Ensure this matches the structure returned by POST /api/widgets and GET /api/widgets/{id}
// You might need to adjust this based on the actual API response shape
export interface ApiWidget {
	id: string
	team_id: string
	program_id: string
	name: string
	type: 'nps' | 'csat' | 'custom' | 'testimonial'
	created_at: string
	updated_at?: string
	is_active: boolean
	// Module Toggles
	enable_survey?: boolean
	enable_bug_reporting?: boolean
	enable_contact_messages?: boolean
	enable_feature_requests?: boolean
	enable_reviews?: boolean
	enable_live_chat?: boolean
	// Appearance
	position?: string
	primary_color?: string
	gradient_end_color?: string | null
	background_color?: string
	text_color?: string
	// Feedback Button
	show_feedback_button?: boolean
	feedback_button_text?: string
	feedback_button_color?: string
	feedback_button_text_color?: string
	// Branding
	show_branding?: boolean
	// Theme configuration stored as JSONB
	theme_config?: {
		menuItemColors?: any
		menuItemStyle?: any
		animation?: any
		glassmorphism?: any
		layout?: any
		brandingStyle?: any
	}
	// Content
	header_title?: string
	primary_feedback_type?: string // Matches base schema, might be same as 'type'
	question?: string
	button_text?: string
	// Custom Questions (nested structure expected from backend)
	custom_questions?: {
		id: string
		type: 'text' | 'email' | 'multiple_choice' | 'scale'
		question: string
		required?: boolean
		options?: string[]
		order_index?: number
		min_scale?: number | null
		max_scale?: number | null
	}[]
	// Thank You Message (assuming JSONB from backend is parsed)
	thank_you?: { title: string; message: string }
	// Targeting and Trigger (assuming JSONB)
	targeting?: any
	trigger?: any
}

// --- Widget API Functions ---

export const getWidgetDetails = (widgetId: string): Promise<ApiWidget> => {
	return apiClient<ApiWidget>(`/api/widgets/${widgetId}`)
}

// Add other widget functions (create, update, delete) here if needed
// export const createWidget = (data: ...) => apiClient('/api/widgets', { method: 'POST', data });
// export const updateWidget = (widgetId: string, data: ...) => apiClient(`/api/widgets/${widgetId}`, { method: 'PUT', data });
// export const deleteWidget = (widgetId: string) => apiClient(`/api/widgets/${widgetId}`, { method: 'DELETE' });

// Agent availability function
export const getAgentAvailability = async (programId: string): Promise<any> => {
	// For public endpoints, we need to construct the full URL manually since apiClient adds auth headers
	const apiUrl = getEffectiveApiUrl();
	const response = await fetch(`${apiUrl}/api/public/agent-availability/${programId}`);
	if (!response.ok) {
		throw new Error(`HTTP error ${response.status}`);
	}
	return response.json();
};

